import React, { useMemo, useState } from 'react';
import { Badge, Box, Collapse, Flex, Spinner, Text, Tooltip } from '@chakra-ui/react';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowsInLineVertical, ArrowsOutLineVertical, CaretRight } from 'phosphor-react';

import {
  IFinanceiroBaseParams,
  ICustoPessoalItem,
  useFinanceiroDreComposicao,
  useFinanceiroLancamentos,
} from '../../../../hooks/useFinanceiro';
import { AnimatedBar } from '../components/AnimatedBar';
import { brl } from '../components/format';
import { InfoPopover } from '../components/InfoPopover';
import { Segmented } from '../components/Segmented';

const ACCENT = '#DB2777';
const DESPESA = '#DC2626';
const INK = '#0F172A';
const MUTED = '#94A3B8';

// Cor semântica por grupo (usada no rail-minimapa).
const COR_GRUPO: Record<string, string> = {
  'Folha/Encargos': '#DC2626',
  Benefícios: '#0EA5E9',
  Estagiários: '#1B88F5',
  Sócios: '#7C3AED',
};
const corGrupo = (nome: string) => COR_GRUPO[nome] ?? '#64748B';

const fmtPct = (v: number) => `${v.toFixed(1).replace('.', ',')}%`;
const pct = (parte: number, todo: number) => (todo > 0 ? (parte / todo) * 100 : 0);
const fmtData = (s: string | null) => {
  if (!s) return '—';
  const [y, m, d] = s.slice(0, 10).split('-');
  return y && m && d ? `${d}/${m}/${y.slice(2)}` : s;
};

// Folha paga em lote ao banco — pessoa_nome vira o cedente, não um colaborador.
const RX_BANCO =
  /\b(banco|santander|itau|itaú|bradesco|caixa|cef|sicoob|sicredi|nubank|inter|folha|lote|pagsal)\b/i;
const ehLote = (nome: string | null) => !!nome && RX_BANCO.test(nome);
const nomePessoa = (nome: string | null) => {
  if (!nome || nome === '—') return 'Sem identificação';
  return nome;
};

type Abertos = {
  grupos: Set<string>;
  categorias: Set<string>;
  pessoas: Set<string>;
};

interface ArvoreProps {
  grupos: { nome: string; itens: ICustoPessoalItem[] }[];
  custoTotal: number;
  receitaTotal: number;
  params: IFinanceiroBaseParams;
  abertos: Abertos;
  toggleGrupo: (nome: string) => void;
  toggleCategoria: (cat: string) => void;
  togglePessoa: (key: string) => void;
  onFoco?: (foco: { categoria: string; valor: number } | null) => void;
}

type Ordem = 'valor' | 'az';
const ordena = <T extends { valor: number }>(arr: T[], ordem: Ordem, nome: (t: T) => string) =>
  [...arr].sort((a, b) => (ordem === 'az' ? nome(a).localeCompare(nome(b)) : b.valor - a.valor));

// ── Nível 3: linha de lançamento (folha) ───────────────────────────────────
function LancamentoRow({
  data,
  descricao,
  valor,
}: {
  data: string;
  descricao: string;
  valor: number;
}) {
  return (
    <Flex
      justify="space-between"
      align="center"
      gap={3}
      fontSize="xs"
      py={1}
      pl={{ base: 10, md: 16 }}
      pr={2}
    >
      <Flex align="center" gap={2} minW={0}>
        <Box w="4px" h="4px" borderRadius="full" bg="#CBD5E1" flexShrink={0} />
        <Text color={MUTED} w="48px" flexShrink={0} className="tabular-nums">
          {fmtData(data)}
        </Text>
        <Text color="#475569" noOfLines={1}>
          {descricao}
        </Text>
      </Flex>
      <Text color={INK} fontWeight={600} whiteSpace="nowrap" className="tabular-nums">
        {brl(valor)}
      </Text>
    </Flex>
  );
}

// Lista de lançamentos — montada SÓ quando a pessoa abre (lazy: a query roda no mount).
function PessoaLancamentosLista({
  params,
  categoria,
  pessoa,
  reduce,
}: {
  params: IFinanceiroBaseParams;
  categoria: string;
  pessoa: string | null;
  reduce: boolean;
}) {
  const lanc = useFinanceiroLancamentos({
    ...params,
    tipo: 'DESPESA',
    categoria,
    pessoa_nome: pessoa ?? '',
    page: 1,
    size: 100,
  });
  const itens = lanc.data?.items ?? [];
  const totalItens = lanc.data?.total ?? itens.length;

  if (lanc.isLoading) {
    return (
      <Flex justify="center" py={2}>
        <Spinner size="sm" color={ACCENT} />
      </Flex>
    );
  }
  if (lanc.isError) {
    return (
      <Text fontSize="xs" color={DESPESA} pl={{ base: 10, md: 16 }} py={2}>
        Não foi possível carregar.{' '}
        <Box as="button" textDecoration="underline" onClick={() => lanc.refetch()}>
          Tentar de novo
        </Box>
      </Text>
    );
  }
  if (itens.length === 0) {
    return (
      <Text fontSize="xs" color={MUTED} pl={{ base: 10, md: 16 }} py={2}>
        Sem lançamentos detalhados.
      </Text>
    );
  }
  return (
    <Box pb={1}>
      {itens.map((l, i) => {
        const row = (
          <LancamentoRow
            data={l.data_competencia || l.data_vencimento || ''}
            descricao={l.descricao || l.documento || 'Lançamento'}
            valor={l.total ?? 0}
          />
        );
        return reduce ? (
          <Box key={l.id}>{row}</Box>
        ) : (
          <motion.div
            key={l.id}
            initial={{ opacity: 0, x: -8 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.2, delay: Math.min(i * 0.02, 0.4) }}
          >
            {row}
          </motion.div>
        );
      })}
      {totalItens > itens.length && (
        <Text fontSize="11px" color={MUTED} pl={{ base: 10, md: 16 }} py={1}>
          Mostrando {itens.length} de {totalItens} lançamentos.
        </Text>
      )}
    </Box>
  );
}

// ── Nível 2: pessoa (lazy lançamentos ao abrir) ─────────────────────────────
function PessoaNode({
  params,
  categoria,
  pessoa,
  valor,
  qtd,
  max,
  total,
  aberto,
  onToggle,
  reduce,
}: {
  params: IFinanceiroBaseParams;
  categoria: string;
  pessoa: string | null;
  valor: number;
  qtd: number;
  max: number;
  total: number;
  aberto: boolean;
  onToggle: () => void;
  reduce: boolean;
}) {
  const lote = ehLote(pessoa);
  return (
    <Box bg={aberto ? `${ACCENT}0A` : 'transparent'} borderRadius="lg" transition="background .15s">
      <Flex
        as="button"
        w="full"
        align="center"
        gap={3}
        pl={{ base: 6, md: 12 }}
        pr={2}
        py={2}
        textAlign="left"
        onClick={onToggle}
        aria-expanded={aberto}
        _hover={{ bg: aberto ? 'transparent' : 'rgba(15,23,42,0.03)' }}
        borderRadius="lg"
      >
        <Box
          as={CaretRight}
          size={12}
          color={aberto ? ACCENT : '#CBD5E1'}
          flexShrink={0}
          transform={aberto ? 'rotate(90deg)' : 'none'}
          transition="transform .18s cubic-bezier(0.4,0,0.2,1)"
        />
        <Box minW={0} flex="1">
          <Flex justify="space-between" align="baseline" gap={3}>
            <Flex align="center" gap={2} minW={0}>
              <Text fontSize="13px" fontWeight={600} color="#334155" noOfLines={1}>
                {nomePessoa(pessoa)}
              </Text>
              {lote && (
                <Tooltip
                  label="Paga ao banco em lote — sem quebra por colaborador. Use a lente 'Por pessoa' para o cruzamento com o Domínio."
                  fontSize="xs"
                  hasArrow
                >
                  <Badge
                    bg="rgba(15,23,42,0.06)"
                    color="#64748B"
                    fontSize="9px"
                    borderRadius="full"
                    px={2}
                  >
                    em lote
                  </Badge>
                </Tooltip>
              )}
            </Flex>
            <Flex align="center" gap={2} whiteSpace="nowrap" flexShrink={0}>
              <Badge
                bg="rgba(15,23,42,0.05)"
                color="#64748B"
                fontSize="9px"
                borderRadius="full"
                px={2}
              >
                {qtd} lanç.
              </Badge>
              <Text
                fontSize="11px"
                color={MUTED}
                w="42px"
                textAlign="right"
                className="tabular-nums"
              >
                {fmtPct(pct(valor, total > 0 ? total : valor))}
              </Text>
              <Text fontSize="13px" fontWeight={700} color={INK} className="tabular-nums">
                {brl(valor)}
              </Text>
            </Flex>
          </Flex>
          <Box mt={1} display={{ base: 'none', md: 'block' }}>
            <AnimatedBar pct={Math.max(3, (valor / (max || 1)) * 100)} cor={ACCENT} h="4px" />
          </Box>
        </Box>
      </Flex>

      <Collapse in={aberto} animateOpacity unmountOnExit>
        {aberto && (
          <PessoaLancamentosLista
            params={params}
            categoria={categoria}
            pessoa={pessoa}
            reduce={reduce}
          />
        )}
      </Collapse>
    </Box>
  );
}

// ── Nível 1: categoria (lazy composição por pessoa ao abrir) ────────────────
function CategoriaNode({
  params,
  item,
  totalGrupo,
  maxCategoria,
  ordem,
  abertos,
  togglePessoa,
  onToggle,
  onFoco,
  reduce,
}: {
  params: IFinanceiroBaseParams;
  item: ICustoPessoalItem;
  totalGrupo: number;
  maxCategoria: number;
  ordem: Ordem;
  abertos: Abertos;
  togglePessoa: (key: string) => void;
  onToggle: () => void;
  onFoco?: (foco: { categoria: string; valor: number } | null) => void;
  reduce: boolean;
}) {
  const aberto = abertos.categorias.has(item.categoria);
  const comp = useFinanceiroDreComposicao(
    { ...params, tipo: 'DESPESA', categoria: item.categoria },
    aberto,
  );
  const pessoas = useMemo(
    () => ordena(comp.data?.itens ?? [], ordem, p => p.pessoa_nome || ''),
    [comp.data, ordem],
  );
  const maxPessoa = Math.max(1, ...pessoas.map(p => p.valor));

  return (
    <Box
      bg={aberto ? `${ACCENT}0D` : 'transparent'}
      borderLeft={aberto ? `2px solid ${ACCENT}` : '2px solid transparent'}
      borderRadius="0 10px 10px 0"
      transition="background .15s"
    >
      <Flex
        as="button"
        w="full"
        align="center"
        gap={3}
        pl={{ base: 4, md: 6 }}
        pr={2}
        py={2.5}
        textAlign="left"
        onClick={onToggle}
        onMouseEnter={() => onFoco?.({ categoria: item.categoria, valor: item.valor })}
        onMouseLeave={() => onFoco?.(null)}
        aria-expanded={aberto}
        _hover={{ bg: aberto ? 'transparent' : 'rgba(15,23,42,0.025)' }}
      >
        <Box
          as={CaretRight}
          size={14}
          color={aberto ? ACCENT : '#CBD5E1'}
          flexShrink={0}
          transform={aberto ? 'rotate(90deg)' : 'none'}
          transition="transform .18s cubic-bezier(0.4,0,0.2,1)"
        />
        <Box minW={0} flex="1">
          <Flex justify="space-between" align="baseline" gap={3}>
            <Flex align="center" gap={2} minW={0}>
              <Text fontSize="14px" fontWeight={600} color={INK} noOfLines={1}>
                {item.categoria}
              </Text>
              {aberto && pessoas.length > 0 && (
                <Badge bg={`${ACCENT}1A`} color={ACCENT} fontSize="9px" borderRadius="full" px={2}>
                  {pessoas.length} {pessoas.length === 1 ? 'pessoa' : 'pessoas'}
                </Badge>
              )}
            </Flex>
            <Flex align="center" gap={2} whiteSpace="nowrap" flexShrink={0}>
              <Text
                fontSize="11px"
                color={MUTED}
                w="44px"
                textAlign="right"
                className="tabular-nums"
              >
                {fmtPct(pct(item.valor, totalGrupo))}
              </Text>
              <Text fontSize="14px" fontWeight={700} color={INK} className="tabular-nums">
                {brl(item.valor)}
              </Text>
            </Flex>
          </Flex>
          <Box mt={1} display={{ base: 'none', md: 'block' }}>
            <AnimatedBar
              pct={Math.max(3, (item.valor / (maxCategoria || 1)) * 100)}
              cor={ACCENT}
              h="5px"
            />
          </Box>
        </Box>
      </Flex>

      <Collapse in={aberto} animateOpacity unmountOnExit>
        {aberto && comp.isLoading && (
          <Flex justify="center" py={3}>
            <Spinner size="sm" color={ACCENT} />
          </Flex>
        )}
        {aberto && !comp.isLoading && comp.isError && (
          <Text fontSize="xs" color={DESPESA} pl={{ base: 6, md: 12 }} py={2}>
            Não foi possível carregar.{' '}
            <Box as="button" textDecoration="underline" onClick={() => comp.refetch()}>
              Tentar de novo
            </Box>
          </Text>
        )}
        {aberto && !comp.isLoading && !comp.isError && pessoas.length === 0 && (
          <Text fontSize="xs" color={MUTED} pl={{ base: 6, md: 12 }} py={2}>
            Sem composição detalhada para esta categoria.
          </Text>
        )}
        {aberto && pessoas.length > 0 && (
          <Box pb={1}>
            {pessoas.map(p => {
              const key = `${item.categoria}|${p.pessoa_nome ?? ''}`;
              return (
                <PessoaNode
                  key={key}
                  params={params}
                  categoria={item.categoria}
                  pessoa={p.pessoa_nome}
                  valor={p.valor}
                  qtd={p.qtd}
                  max={maxPessoa}
                  total={item.valor}
                  aberto={abertos.pessoas.has(key)}
                  onToggle={() => togglePessoa(key)}
                  reduce={reduce}
                />
              );
            })}
          </Box>
        )}
      </Collapse>
    </Box>
  );
}

// ── Nível 0: grupo (categorias já em memória) ───────────────────────────────
function GrupoSection({
  grupo,
  itens,
  custoTotal,
  ordem,
  params,
  abertos,
  toggleGrupo,
  toggleCategoria,
  togglePessoa,
  onFoco,
  reduce,
}: {
  grupo: string;
  itens: ICustoPessoalItem[];
  custoTotal: number;
  ordem: Ordem;
  params: IFinanceiroBaseParams;
  abertos: Abertos;
  toggleGrupo: (nome: string) => void;
  toggleCategoria: (cat: string) => void;
  togglePessoa: (key: string) => void;
  onFoco?: (foco: { categoria: string; valor: number } | null) => void;
  reduce: boolean;
}) {
  const aberto = abertos.grupos.has(grupo);
  const totalGrupo = itens.reduce((s, i) => s + i.valor, 0);
  const cats = useMemo(() => ordena(itens, ordem, i => i.categoria), [itens, ordem]);
  const maxCategoria = Math.max(1, ...cats.map(c => c.valor));

  return (
    <Box>
      <Flex
        as="button"
        w="full"
        align="center"
        gap={3}
        pl={4}
        pr={2}
        py={2.5}
        textAlign="left"
        onClick={() => toggleGrupo(grupo)}
        aria-expanded={aberto}
        _hover={{ bg: 'rgba(15,23,42,0.02)' }}
        borderRadius="lg"
      >
        <Box
          as={CaretRight}
          size={14}
          color={aberto ? ACCENT : '#94A3B8'}
          flexShrink={0}
          transform={aberto ? 'rotate(90deg)' : 'none'}
          transition="transform .18s cubic-bezier(0.4,0,0.2,1)"
        />
        <Box minW={0} flex="1">
          <Flex justify="space-between" align="baseline" gap={3}>
            <Flex align="center" gap={2}>
              <Box w="8px" h="8px" borderRadius="sm" bg={corGrupo(grupo)} flexShrink={0} />
              <Text fontSize="15px" fontWeight={700} color={INK}>
                {grupo}
              </Text>
              <Badge
                bg="rgba(15,23,42,0.05)"
                color="#64748B"
                fontSize="9px"
                borderRadius="full"
                px={2}
              >
                {cats.length} {cats.length === 1 ? 'categoria' : 'categorias'}
              </Badge>
            </Flex>
            <Flex align="center" gap={2} whiteSpace="nowrap">
              <Text
                fontSize="11px"
                color={MUTED}
                w="44px"
                textAlign="right"
                className="tabular-nums"
              >
                {fmtPct(pct(totalGrupo, custoTotal))}
              </Text>
              <Text fontSize="14px" fontWeight={800} color={DESPESA} className="tabular-nums">
                {brl(totalGrupo)}
              </Text>
            </Flex>
          </Flex>
        </Box>
      </Flex>

      <Collapse in={aberto} animateOpacity unmountOnExit>
        <Box pb={1}>
          {cats.map(it => (
            <CategoriaNode
              key={it.categoria}
              params={params}
              item={it}
              totalGrupo={totalGrupo}
              maxCategoria={maxCategoria}
              ordem={ordem}
              abertos={abertos}
              togglePessoa={togglePessoa}
              onToggle={() => toggleCategoria(it.categoria)}
              onFoco={onFoco}
              reduce={reduce}
            />
          ))}
        </Box>
      </Collapse>
    </Box>
  );
}

// ── Rail empilhado: minimapa da composição por grupo ────────────────────────
function RailComposicao({
  grupos,
  custoTotal,
  receitaTotal,
}: {
  grupos: { nome: string; itens: ICustoPessoalItem[] }[];
  custoTotal: number;
  receitaTotal: number;
}) {
  const segs = grupos
    .map(g => ({ nome: g.nome, valor: g.itens.reduce((s, i) => s + i.valor, 0) }))
    .filter(s => s.valor > 0);
  return (
    <Box>
      <Flex h="8px" borderRadius="full" overflow="hidden" bg="rgba(15,23,42,0.05)">
        {segs.map(s => (
          <Tooltip
            key={s.nome}
            label={`${s.nome} · ${brl(s.valor)} · ${fmtPct(pct(s.valor, receitaTotal))} da receita`}
            fontSize="xs"
            hasArrow
          >
            <Box w={`${pct(s.valor, custoTotal)}%`} bg={corGrupo(s.nome)} h="full" />
          </Tooltip>
        ))}
      </Flex>
    </Box>
  );
}

export function CustoArvore({
  grupos,
  custoTotal,
  receitaTotal,
  params,
  abertos,
  toggleGrupo,
  toggleCategoria,
  togglePessoa,
  onFoco,
}: ArvoreProps) {
  const reduce = !!useReducedMotion();
  const [ordem, setOrdem] = useState<Ordem>('valor');
  const totalCategorias = grupos.reduce((s, g) => s + g.itens.length, 0);
  const todosGruposAbertos = grupos.every(g => abertos.grupos.has(g.nome));

  const toggleTodos = () =>
    grupos.forEach(g => {
      if (todosGruposAbertos ? abertos.grupos.has(g.nome) : !abertos.grupos.has(g.nome)) {
        toggleGrupo(g.nome);
      }
    });

  if (grupos.length === 0) {
    return (
      <Text fontSize="sm" color={MUTED} textAlign="center" py={10}>
        Sem lançamentos de pessoal no período. Tente ampliar o período no filtro acima.
      </Text>
    );
  }

  return (
    <Box>
      {/* Cabeçalho da árvore */}
      <Flex justify="space-between" align="flex-start" gap={3} wrap="wrap" mb={3}>
        <Box minW={0} flex="1">
          <Flex align="center" gap={2} wrap="wrap">
            <Text fontSize="15px" fontWeight={700} color={INK}>
              Custo total de pessoal: {brl(custoTotal)}
            </Text>
            <Text fontSize="13px" color="#64748B">
              · {fmtPct(pct(custoTotal, receitaTotal))} da receita · {totalCategorias} categorias em{' '}
              {grupos.length} grupos
            </Text>
            <InfoPopover title="Composição do custo">
              Abra um grupo para ver as categorias; abra uma categoria para ver as pessoas e os
              lançamentos que compõem o valor. Os valores são o desembolso real do Conta Azul.
            </InfoPopover>
          </Flex>
        </Box>
        <Flex align="center" gap={2} flexShrink={0}>
          <Segmented
            value={ordem}
            onChange={v => setOrdem(v as Ordem)}
            accent={ACCENT}
            options={[
              { value: 'valor', label: 'Maior valor' },
              { value: 'az', label: 'A–Z' },
            ]}
          />
          <Flex
            as="button"
            align="center"
            gap={1}
            px={3}
            h="32px"
            borderRadius="full"
            bg="rgba(15,23,42,0.04)"
            color="#475569"
            fontSize="12px"
            fontWeight={600}
            _hover={{ bg: 'rgba(15,23,42,0.07)' }}
            onClick={toggleTodos}
          >
            <Box as={todosGruposAbertos ? ArrowsInLineVertical : ArrowsOutLineVertical} size={14} />
            {todosGruposAbertos ? 'Recolher' : 'Expandir'}
          </Flex>
        </Flex>
      </Flex>

      {/* Rail-minimapa */}
      <Box mb={4}>
        <RailComposicao grupos={grupos} custoTotal={custoTotal} receitaTotal={receitaTotal} />
      </Box>

      {/* Árvore */}
      <Flex direction="column" gap={1}>
        {grupos.map(g => (
          <GrupoSection
            key={g.nome}
            grupo={g.nome}
            itens={g.itens}
            custoTotal={custoTotal}
            ordem={ordem}
            params={params}
            abertos={abertos}
            toggleGrupo={toggleGrupo}
            toggleCategoria={toggleCategoria}
            togglePessoa={togglePessoa}
            onFoco={onFoco}
            reduce={reduce}
          />
        ))}
      </Flex>
    </Box>
  );
}
